// WebSocket glue for one canvas room: fetch a ticket, open the socket, feed CanvasSync, ping every
// 25 s, reconnect with backoff (a new ticket each time). Socket and timers are injected for tests.
import { backoffMs, shouldReconnect, type Timers } from "./presence.ts";
import type { CanvasSync, ClientMsg, ServerMsg } from "./sync.ts";

export interface SessionInfo {
  ticket: string;
  path: string;
  canWrite: boolean;
}

export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export const PING_MS = 25_000;
const OPEN = 1;

/**
 * `ws(s)://<api origin><path>?ticket=…`. The origin is the API base's when that is absolute,
 * else the page's (the dev proxy and the Node server both serve the socket next to /v1).
 */
export function socketUrl(apiBase: string, path: string, ticket: string, pageOrigin: string): string {
  let origin = pageOrigin;
  try {
    origin = new URL(apiBase).origin;
  } catch {
    // relative base such as /v1: same origin as the page
  }
  const u = new URL(path, origin);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.search = "";
  u.searchParams.set("ticket", ticket);
  return u.toString();
}

export interface ConnectionOptions {
  getSession: () => Promise<SessionInfo>;
  createSocket: (url: string) => SocketLike;
  apiBase: string;
  pageOrigin: string;
  timers: Timers & {
    setInterval: (fn: () => void, ms: number) => unknown;
    clearInterval: (h: unknown) => void;
  };
  rand?: () => number;
  onState?: (s: { connected: boolean; attempt: number; error?: string }) => void;
}

export class CanvasConnection {
  private sync!: CanvasSync;
  private ws: SocketLike | null = null;
  private stopped = true;
  private attempt = 0;
  private retry: unknown = null;
  private ping: unknown = null;
  private epoch = 0;
  private readonly o: ConnectionOptions;

  constructor(options: ConnectionOptions) {
    this.o = options;
  }

  bind(sync: CanvasSync): void {
    this.sync = sync;
  }

  get open(): boolean {
    return this.ws?.readyState === OPEN;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.epoch++;
    if (this.retry !== null) this.o.timers.clearTimeout(this.retry);
    this.retry = null;
    this.clearPing();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      try {
        ws.close(1000, "bye");
      } catch {
        // already closed
      }
      this.sync.disconnected();
    }
  }

  /** Drops frames while the socket is not open: presence is ephemeral and ops wait in `pending`. */
  send(msg: ClientMsg): void {
    if (this.ws?.readyState !== OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  private async connect(): Promise<void> {
    const epoch = ++this.epoch;
    let session: SessionInfo;
    try {
      session = await this.o.getSession();
    } catch (e) {
      if (epoch !== this.epoch || this.stopped) return;
      this.o.onState?.({
        connected: false,
        attempt: this.attempt,
        error: e instanceof Error ? e.message : String(e),
      });
      return this.scheduleRetry();
    }
    if (epoch !== this.epoch || this.stopped) return;
    this.sync.canWrite = session.canWrite;
    const ws = this.o.createSocket(
      socketUrl(this.o.apiBase, session.path, session.ticket, this.o.pageOrigin),
    );
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.sync.connected();
      this.clearPing();
      this.ping = this.o.timers.setInterval(() => this.send({ t: "ping" }), PING_MS);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || typeof ev.data !== "string") return;
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data) as ServerMsg;
      } catch {
        return;
      }
      if (msg.t === "welcome") {
        this.attempt = 0;
        this.o.onState?.({ connected: true, attempt: 0 });
      }
      this.sync.receive(msg);
    };
    ws.onerror = () => {
      // the close event follows
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearPing();
      this.sync.disconnected();
      this.o.onState?.({ connected: false, attempt: this.attempt });
      if (!this.stopped && shouldReconnect(ev.code)) this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (this.stopped || this.retry !== null) return;
    const ms = backoffMs(this.attempt++, (this.o.rand ?? Math.random)());
    this.retry = this.o.timers.setTimeout(() => {
      this.retry = null;
      if (!this.stopped) void this.connect();
    }, ms);
  }

  private clearPing(): void {
    if (this.ping !== null) this.o.timers.clearInterval(this.ping);
    this.ping = null;
  }
}
